import { useLang } from 'hooks/lang';

import { loaders } from 'entities';

import { MultipleEmailsInput } from 'components/Inputs';
import { Column, Row, Checkbox, LabeledTextarea, LabeledInput, Label } from 'components/Blocks';

import { InvoiceDate } from './Date';
import { ClientBillingReference } from './BillingReference';

import { FormControlHook } from 'hooks';

import { pick } from 'utils/object';

import { InvoiceCreationProps } from './types';

interface BillingProps
  extends Pick<FormControlHook<InvoiceCreationProps>, 'state' | 'dispatch' | 'handleChange'> {
  children?: never;
  client: string | null;
  clientName: string;
}

export function InvoiceBillingForm({
  state,
  dispatch,
  client,
  clientName,
  handleChange,
}: BillingProps): JSX.Element {
  const { t } = useLang();

  const { data, loading } = loaders.clients.billing.use({
    blockLoad: !client,

    params: { id: client! },

    onDataChange: (data) => dispatch(pick(data, ['emails', 'companyName'])),
  });

  return (
    <Row alignItems="flex-start" className="mid_tablet:flex-col">
      <Column fullWidth>
        <LabeledInput
          label={t('tax-id')}
          value={state.taxId}
          loading={loading}
          name="taxId"
          onChange={handleChange}
        />

        <Row gap="2" relative>
          <LabeledInput
            label="PO"
            value={state.poNumber}
            loading={loading}
            name="poNumber"
            onChange={handleChange}
            fullWidth
            className="pr-20"
          />

          {!loading && (
            <Row className="absolute bottom-2 right-2" gap="2">
              <Checkbox
                checked={state.poNumber === 'N/A'}
                size="small"
                onChange={() =>
                  dispatch({ poNumber: ({ poNumber }) => (poNumber === 'N/A' ? '' : 'N/A') })
                }
              />
              <Label className="text-sm">N/A</Label>
            </Row>
          )}
        </Row>

        <LabeledInput
          label={t('client-pm')}
          value={state.clientPM}
          loading={loading}
          name="clientPM"
          onChange={handleChange}
        />

        <LabeledInput
          label={t('company-name')}
          value={state.companyName}
          loading={loading}
          name="companyName"
          onChange={handleChange}
        />

        <LabeledInput
          label={t('address')}
          value={state.address}
          loading={loading}
          name="address"
          onChange={handleChange}
        />

        <InvoiceDate state={state} dispatch={dispatch} />

        <MultipleEmailsInput
          emails={state.emails}
          loading={loading}
          updateEmails={(emails) => dispatch({ emails })}
        />

        <LabeledTextarea
          label={t('invoice-description') as string}
          value={state.description}
          loading={loading}
          name="description"
          onChange={handleChange}
          resize
        />

        <LabeledTextarea
          label={t('comments') as string}
          value={state.comment}
          loading={loading}
          name="comment"
          onChange={handleChange}
          resize
        />
      </Column>

      <ClientBillingReference data={data} loading={loading} clientName={clientName} />
    </Row>
  );
}
